import { randomDelay } from "@/lib/utils/sleep";
import {
  listInvitedBrokers,
  getSeatSummary,
  consumeSeatForInvite,
  resendInvite as resendInviteSeat,
  revokeInvite as revokeInviteSeat,
  type AgencySeatSummary,
} from "@/lib/mocks/agencySeats";

export type { AgencySeatSummary } from "@/lib/mocks/agencySeats";

/**
 * Tipo do vínculo do corretor com a imobiliária (ADR-0039). Os dois são exclusivos —
 * a licença que dá acesso é paga pela imobiliária. O que muda é a exclusividade da
 * CAPTAÇÃO: `internal` capta para a casa, `external` mantém a carteira própria privada.
 */
export type BrokerLinkType = "internal" | "external";

export interface AgencyBrokerListItem {
  id: string;
  name: string;
  email: string;
  hasActiveLicense: boolean;
  status: "active" | "pending_license" | "invited";
  /** Nulo apenas nas linhas do roster anteriores ao hub#686. */
  linkType?: BrokerLinkType | null;
  joinedAt: string;
  productionVgv: number;
}

export interface BrokerPerformance {
  brokerId: string;
  funnelData: {
    stage: string;
    count: number;
  }[];
  closedDealsValue: number;
  commissionsEarned: number;
}

// Licenças liberadas em runtime: corretores `pending_license` viram `active`
// quando a imobiliária libera a licença (botão na lista de Corretores).
const activatedIds = new Set<string>();

function applyLicenses(brokers: AgencyBrokerListItem[]): AgencyBrokerListItem[] {
  return brokers.map((b) =>
    activatedIds.has(b.id)
      ? { ...b, status: "active", hasActiveLicense: true }
      : b,
  );
}

export async function listBrokers(): Promise<AgencyBrokerListItem[]> {
  await randomDelay();
  // Convites emitidos em runtime (seat-store) aparecem no topo como "Convite Enviado".
  return applyLicenses([
    ...listInvitedBrokers(),
    {
      id: "b1",
      name: "Carlos Ferreira",
      email: "carlos@imobiliaria.com",
      hasActiveLicense: true,
      status: "active",
      joinedAt: "2025-01-10T10:00:00Z",
      productionVgv: 2500000.00,
    },
    {
      id: "b2",
      name: "Rafaela Souza",
      email: "rafaela@imobiliaria.com",
      hasActiveLicense: true,
      status: "active",
      joinedAt: "2025-02-15T14:30:00Z",
      productionVgv: 1850000.00,
    },
    {
      id: "b3",
      name: "Marcos Oliveira",
      email: "marcos@imobiliaria.com",
      hasActiveLicense: false,
      status: "pending_license",
      joinedAt: "2025-05-20T09:00:00Z",
      productionVgv: 0,
    },
  ]);
}

// Libera a licença de um corretor `pending_license` → vira `active`. (No back,
// equivale ao POST /v1/licenses/activate.)
export async function activateLicense(brokerId: string): Promise<void> {
  await randomDelay(500, 1200);
  activatedIds.add(brokerId);
}

// ─── Seats do plano + ciclo de vida do convite (#163) ──────────────────────
export async function getSeats(): Promise<AgencySeatSummary> {
  await randomDelay();
  return getSeatSummary();
}

// Convidar consome um seat do plano (regra do #162). Propaga
// NoSeatsAvailableError quando o plano está no limite, pra UI bloquear/avisar.
export async function inviteBroker(
  email: string,
  linkType: BrokerLinkType,
): Promise<AgencyBrokerListItem> {
  await randomDelay(800, 1500);
  return consumeSeatForInvite(email, linkType);
}

export async function resendInvite(inviteId: string): Promise<void> {
  await randomDelay(500, 1000);
  resendInviteSeat(inviteId);
}

// Revogar devolve o seat ao plano.
export async function revokeInvite(inviteId: string): Promise<void> {
  await randomDelay(500, 1000);
  revokeInviteSeat(inviteId);
}

export async function getBrokerPerformance(id: string): Promise<BrokerPerformance> {
  await randomDelay();
  return {
    brokerId: id,
    // Espelha as 5 etapas que o crm-api devolve de verdade (hub#687).
    funnelData: [
      { stage: "Leads", count: 120 },
      { stage: "Visitas", count: 45 },
      { stage: "Propostas", count: 12 },
      { stage: "Negócios", count: 8 },
      { stage: "Contratos", count: 5 },
    ],
    closedDealsValue: 2500000.00,
    commissionsEarned: 75000.00,
  };
}
